import React, { lazy, Suspense, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';
import { getIsPWAInstalled } from 'helpers/getIsPWAInstalled';
import { isCasinoGamePage } from 'utils';

import {
  printMissingConfigError,
  SiteConfigManager,
  useSettings,
  useUserMessages,
} from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';

import { useLocalBetslip } from 'common/Betslip/LocalBetslipContext';
import { defaultRoutes } from 'components/MainRouter/constants';
import { useMessagesTabConfig } from 'src/hooks/useMessagesTabConfig';

import NavigationBarItemList from './components/NavigationBarItemList';
import NavigationNotchBackground from './components/NavigationNotchBackground';
import { useFloatingNotch } from './hooks/useFloatingNotch';
import { isSupportedLinkPlatform } from './utils';

const BetslipPopupButton = lazy(
  () => import(/* webpackChunkName: "Betslip" */ 'common/Betslip/BetslipPopupButton'),
);

const { CLIENT_PROFILE, MENU } = COMPONENT_TYPES;

const { account: accountDefaultRoute, menu: menuDefaultRoute } = defaultRoutes;

const DefaultBetslipNavigationBar = () => {
  // state of the betslip popup
  const { betIdToEdit } = useLocalBetslip();
  const { quickBet: isQuickBet } = useSettings();
  const { unreadMessagesCount } = useUserMessages();
  const navigation = SiteConfigManager.getConfig('navigation.top', []);
  const casinoPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO);
  const casinoLivePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO_LIVE);
  const {
    location: { pathname },
  } = useHistory();
  const messagesTab = useMessagesTabConfig();

  const iconAccount = useMemo(
    () => ({
      icon: 'icon-account',
      iconType: 'predefined',
      name: 'client profile',
      path: SiteConfigManager.getComponentPath(CLIENT_PROFILE) || accountDefaultRoute,
    }),
    [],
  );

  const links = useMemo(
    () =>
      navigation
        .filter(el => isSupportedLinkPlatform(el))
        .map(
          ({
            alt_icon: altIcon,
            button_style: buttonStyle,
            icon,
            icon_type: iconType,
            light_icon: lightIcon,
            location,
            name,
          }) => ({
            altIcon,
            buttonStyle,
            icon,
            iconType,
            lightIcon,
            name,
            path: location,
          }),
        )
        .sort((a, b) => (a.order_index > b.order_index ? -1 : 1)),
    [navigation],
  );

  const leftLinks = useMemo(() => {
    const linksToRender = [links[0] || iconAccount, links[1] || iconAccount];

    return <NavigationBarItemList links={linksToRender} />;
  }, [links, iconAccount]);

  const rightLinks = useMemo(() => {
    const linksToRender = [
      links[2] || iconAccount,
      {
        icon: 'i-menu',
        iconType: 'predefined',
        name: 'Menu',
        path: SiteConfigManager.getComponentPath(MENU) || menuDefaultRoute,
        withNotification: !!unreadMessagesCount && messagesTab?.isActive,
      },
    ];

    return <NavigationBarItemList links={linksToRender} />;
  }, [links, messagesTab?.isActive, unreadMessagesCount, iconAccount]);

  const hasHighlightedButton = useMemo(() => {
    return links.slice(0, 3).some(link => link.buttonStyle === 'highlighted');
  }, [links]);

  const floatingLink = useMemo(
    () => links.slice(0, 3).find(link => link.buttonStyle === 'floating'),
    [links],
  );
  const hasFloatingButton = !!floatingLink;

  const { notch, tabBarRef } = useFloatingNotch(hasFloatingButton, floatingLink?.path);

  const navigationClass = cx('navigation', {
    hidden: isCasinoGamePage(pathname, [casinoPath, casinoLivePath]),
    'navigation--has-floating': hasFloatingButton,
    'navigation--has-highlighted': hasHighlightedButton,
    shadow: isQuickBet || betIdToEdit,
  });
  const navBarClass = cx('navigation-tab-bar', 'betslip', { 'is-pwa': getIsPWAInstalled() });

  if (navigation.length === 0) {
    printMissingConfigError('navigation.top');
  }

  return (
    <div className={navigationClass}>
      <div className={navBarClass} ref={tabBarRef}>
        {notch && <NavigationNotchBackground {...notch} />}
        {leftLinks}
        <Suspense fallback={<div />}>
          <BetslipPopupButton />
        </Suspense>
        {rightLinks}
      </div>
    </div>
  );
};

export default DefaultBetslipNavigationBar;
